import React, { useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, Bell, LogOut, Plane } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { describeState, fmtIst, fmtIstDate } from "@lawyer/components/mobile/statusUtils";

const confirmLeave = (e) => {
  if ((document.body.dataset.unsaved === "1" || document.body.dataset.unsavedOverride === "1")
    && !window.confirm("You have unsaved availability changes. Leave without saving?")) e.preventDefault();
};

export function MobileHeader({ onMenu, alertCount = 0, initials, photo }) {
  return (
    <header className="lg:hidden sticky top-0 inset-x-0 z-30 bg-[#0A2540] text-white h-14 px-3 flex items-center gap-2 border-b border-white/10" data-testid="m-header">
      <button type="button" onClick={onMenu} aria-label="Open menu" className="h-10 w-10 grid place-items-center rounded-lg active:bg-white/10" data-testid="lawyer-sidebar-toggle">
        <Menu size={22} />
      </button>
      <Link to={LAWYER_BASE} className="flex-1 font-extrabold tracking-tight text-[15px]" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</Link>
      <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} aria-label="Bookings" className="relative h-10 w-10 grid place-items-center rounded-lg active:bg-white/10" data-testid="m-bell">
        <Bell size={20} />
        {alertCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-[10px] font-extrabold grid place-items-center" data-testid="m-bell-badge">
            {alertCount > 9 ? "9+" : alertCount}
          </span>
        )}
      </Link>
      <Link to={`${LAWYER_BASE}/profile`} aria-label="Profile" className="h-9 w-9 rounded-full bg-[#D4AF37] text-[#0A2540] grid place-items-center text-xs font-extrabold overflow-hidden" data-testid="m-avatar">
        {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : initials}
      </Link>
    </header>
  );
}

function DrawerStatus({ listed, state, availability, vacationUntil }) {
  if (listed === null || listed === undefined) return <div className="h-4 w-28 rounded bg-white/10 animate-pulse" />;
  if (!listed) return <p className="text-xs font-bold text-slate-300">Not listed yet</p>;
  const d = describeState(state, availability);
  const text = { emerald: "text-emerald-300", rose: "text-rose-300", amber: "text-amber-300", slate: "text-slate-300" }[d.tone];
  const detail = state === "vacation" ? (vacationUntil ? `Back on ${fmtIstDate(vacationUntil)}` : null)
    : state === "online_scheduled" && availability?.scheduled_until ? `Available until ${fmtIst(availability.scheduled_until)}`
    : state === "online_manual" && availability?.manual_online_until ? `Online until ${fmtIst(availability.manual_online_until)}`
    : d.detail;
  return (
    <div data-testid="m-drawer-status" data-state={state}>
      <p className={`text-xs font-extrabold tracking-wide inline-flex items-center gap-2 ${text}`}>
        {state === "vacation" ? <Plane size={12} /> : <span className={`h-2.5 w-2.5 rounded-full ${d.dot}`} />}
        {d.label.replace("ONLINE · Manual", "ONLINE · Accepting bookings").replace("IN CONSULTATION", "BUSY · In consultation")}
      </p>
      {detail && <p className="mt-0.5 text-[11px] text-slate-400">{detail}</p>}
    </div>
  );
}

export function MobileDrawer({ open, onClose, nav, listed, state, availability, vacationUntil, user, initials, onLogout }) {
  const location = useLocation();
  // Close on navigation; lock page scroll only while the drawer is open.
  useEffect(() => { onClose(); }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <div className={`lg:hidden fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} data-testid="m-drawer-backdrop" />
      <aside
        className={`absolute top-0 left-0 h-full w-[82vw] max-w-[320px] bg-[#0A2540] text-white flex flex-col shadow-2xl transition-transform duration-200 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="m-drawer" role="dialog" aria-label="Navigation"
      >
        <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-white/10">
          <div className="h-10 w-10 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0A2540] font-extrabold">A</div>
          <div className="leading-tight">
            <p className="font-extrabold" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</p>
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Legal Help, Simplified</p>
          </div>
        </div>
        <div className="px-4 py-3 border-b border-white/10">
          <DrawerStatus listed={listed} state={state} availability={availability} vacationUntil={vacationUntil} />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {nav.map(({ to, label, icon: Icon, exact }) => (
            <NavLink key={label} to={`${LAWYER_BASE}/${to}`} end={exact} onClick={confirmLeave} data-testid={`m-nav-${label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold ${isActive ? "bg-[#D4AF37] text-[#0A2540]" : "text-slate-200 active:bg-white/10"}`}>
              <Icon size={18} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="m-3 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] text-[#0A2540] grid place-items-center font-extrabold text-sm">{initials}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" data-testid="m-drawer-user-name">{user?.name || user?.email}</p>
            <p className="text-[10px] text-slate-400">Lawyer</p>
          </div>
          <button type="button" onClick={onLogout} className="h-9 w-9 grid place-items-center text-slate-300" title="Sign out" data-testid="m-logout-btn"><LogOut size={16} /></button>
        </div>
      </aside>
    </div>
  );
}
